import { useMemo, useState } from 'react';
import { useRouter } from 'next/router';

import { Box, Heading, Text, TextButton } from '@zillow/constellation-10';

import postingUtils from '../../../../../../srcLegacy/shared/utils/postingUtils';
import { useGetListingDetails, useGetListingRecommendations } from '../../../../../hooks/queries/properties';
import accessUtils from '../../../../../utils/accessUtils';
import numberUtils from '../../../../../utils/numberUtils';
import propertyUtils from '../../../../../utils/propertyUtils';
import { getRoomForRentFlow } from '../../../../../utils/roomForRentUtils';
import stringHelper from '../../../../../utils/stringHelper';
import { getIsInputFormDisabled } from '../../../../MFAccordion/utils';

import { TRIGGER_LOCATIONS } from '../../../../../constants/ClickstreamConstants';
import { REVIEW_PAGE_SECTIONS } from '../../../../../constants/ListingDetailConstants';
import { RECS } from '../../../../../constants/RecommendationsConstants';
import { SF_MODAL_COMPONENTS } from '../constants';

import RMWPopover from '../../../../../../srcLegacy/shared/components/RMWPopover';
import AccordionIconError from '../../../../../../srcLegacy/shared/components/WizardModularListing/Review/components/AccordionIconError';
import FieldValueDisplay from '../../../../../../srcLegacy/shared/components/WizardModularListing/Review/components/FieldValueDisplay';
import InfoPopover from '../../../../../../srcLegacy/shared/components/WizardModularListing/SpecialOffers/components/InfoPopover';
import RecommendationModal from '../../../../modals/RecommendationModal';
import ContentSectionEditButton from '../components/ContentSectionEditButton';
import { baseRentPopoverContents } from '../components/PropertyCard';

import { iconPlaceholderStyles, reviewSectionStyles, reviewSectionTitleStyles, reviewTitleRowStyles } from '../styles';

import { useAuth } from '../../../../AuthProvider';
import RecommendationIconBulb from '../../../../PostingPath/RecommendationIconBulb';
import SpecialOfferItem from '../../../../PostingPath/SpecialOffers/SpecialOfferItem';
import { FlowType } from '../../../types';

const ariaLabels = {
  MONTHLY_RENT: 'monthly-rent',
  UTILITIES: 'utilities',
  SECURITY_DEPOSIT: 'security-deposit',
  DATE_AVAILABLE: 'date-available',
  RENTERS_INSURANCE: 'renters-insurance',
  ROOMMATE_COUNT: 'roommate-count',
  ROOMMATE_DESCRIPTION: 'roommate-description',
  RENTER_AGE_AND_GENDER_PREFERRED: 'renter-age-and-gender-preferred',
  PETS_CURRENTLY_ON_THE_PROPERTY: 'pets-currently-on-the-property',
  LEASE_TERMS: 'lease-terms',
  LEASE_DURATION: 'lease-duration',
  SPECIAL_OFFERS: 'special-offers',
};

const ROOMMATE_COUNT_DISPLAY_MAP = {
  roommateMaleCount: 'male',
  roommateFemaleCount: 'female',
  roommateNonBinaryCount: 'non-binary',
  roommatePreferNotToIdentifyCount: 'prefer not to identify',
  roommateTotalCount: 'count',
};

const CURRENT_PETS_DISPLAY_MAP = {
  currentCatsCount: (value: string) => {
    return stringHelper.pluralize(value, 'cat', 'cats');
  },
  currentDogsCount: (value: string) => {
    return stringHelper.pluralize(value, 'dog', 'dogs');
  },
};

const PET_POLICY = {
  none: 'No pets allowed',
  // deprecated
  cats: 'Cats allowed',
  catsDto: 'Cats allowed',
  // deprecated
  smallDogs: 'Small dogs allowed',
  smallDogsDto: 'Small dogs allowed',
  // deprecated
  largeDogs: 'Large dogs allowed',
  largeDogsDto: 'Large dogs allowed',
};

const RentDetailsContent = ({
  onEditClick,
}: {
  onEditClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}) => {
  const router = useRouter();
  const { user } = useAuth();

  const listingAlias = router.query.listingAlias as string;

  const { data: listingDetails } = useGetListingDetails(listingAlias);
  const { recommendationsData, recommendationsBySection } = useGetListingRecommendations({
    listingAlias,
    isMultifamily: false,
  });

  const [displayRecsModal, setDisplayRecsModal] = useState('');
  const recommendationTypes = new Set(recommendationsData?.recommendations.map((r) => r.type) ?? []);

  const floorplans = listingDetails?.floorPlans || [];
  const floorplan = floorplans[0];
  const canAccessScreeningCriteria = listingDetails?.listingCriteria?.isEnabled || false;
  const isFeed = listingDetails?.isFeed || false;
  const petsAllowed = listingDetails?.petsAllowed;

  const customNoneFieldValueText = isFeed ? 'None' : 'Action needed';
  const isDisabled = getIsInputFormDisabled(listingDetails, user?.features?.canUpdateListing ?? false);
  const isRoomForRent = propertyUtils.isRoomForRent({ listing: listingDetails });
  const isPetsAllowedEmpty = propertyUtils.getIsPetsAllowedEmpty(listingDetails?.petsAllowed);
  const roomForRentFlow = getRoomForRentFlow(listingDetails?.forRentByType);
  const price = postingUtils.formatPriceFromApi(floorplan.lowPrice?.toString(), floorplan.highPrice?.toString());

  const getMonthlyRentDisplay = () => {
    const displayPrice = `${numberUtils.asCurrency(Number(price))} /month`;

    return price ? displayPrice : '';
  };

  const getSecurityDepositDisplay = () => {
    if (floorplan?.securityDeposit === undefined || floorplan?.securityDeposit === null) {
      return '';
    }
    return numberUtils.asCurrencyWithDefault(floorplan.securityDeposit, '', '$0,0');
  };

  const displayPetsAvailable = useMemo(() => {
    if (!petsAllowed || canAccessScreeningCriteria) {
      return;
    }

    const relevantEntries = Object.entries(petsAllowed).filter(([key]) => {
      // exclude these keys
      return !['cats', 'dogs', 'largeDogs'].includes(key);
    });

    const foundArr = relevantEntries
      .map(([key, value]) => {
        const { allowed = false } = value;

        return allowed ? PET_POLICY[key as keyof typeof PET_POLICY] : null;
      })
      // removes null values
      .filter(Boolean);

    if (
      foundArr.length === 0 &&
      petsAllowed?.catsDto?.allowed === false &&
      petsAllowed?.smallDogsDto?.allowed === false &&
      petsAllowed?.largeDogsDto?.allowed === false
    ) {
      return 'No pets allowed';
    }

    return foundArr.join(', ');
  }, [canAccessScreeningCriteria, petsAllowed]);

  const displayPetsHeading = isRoomForRent ? 'Pets allowed' : 'Pets';
  const ariaLabelDisplayPets = displayPetsHeading.toLowerCase().split(' ').join('-');

  const isRyanFlow = roomForRentFlow === FlowType.ROOM_FOR_RENT_RYAN;

  const roommateCountComponentKey = SF_MODAL_COMPONENTS.RoommateCountFormField.componentKey;

  const displayRoommateCount = (() => {
    const displayArr: Array<string> = [];
    const roomateDetails = listingDetails?.roomForRent?.currentRoommatesDetails;

    // loop through DISPLAY_MAP to specify display order
    Object.keys(ROOMMATE_COUNT_DISPLAY_MAP).forEach((key) => {
      const value = roomateDetails?.[key as keyof typeof ROOMMATE_COUNT_DISPLAY_MAP];

      // accepts a zero (0) value
      if (value !== '' && value !== null && value !== undefined) {
        const displayItem = `${value} ${ROOMMATE_COUNT_DISPLAY_MAP[key as keyof typeof ROOMMATE_COUNT_DISPLAY_MAP]}`;
        displayArr.push(displayItem);
      }
    });

    return displayArr.join(', ');
  })();

  const genderPreferred = (() => {
    const kebabList = (listingDetails?.roomForRent?.roommateRequirements?.roommateGenderPreferences || []).map(
      (string) => {
        return stringHelper.kebabize(string);
      },
    );

    const genderPreferred = kebabList.length === 2 ? `${kebabList[0]} or ${kebabList[1]}` : kebabList.join(', ');

    return genderPreferred.length > 0
      ? `${genderPreferred[0].toUpperCase()}${genderPreferred.slice(1)}`
      : genderPreferred;
  })();

  const roommateMinAgePreference = listingDetails?.roomForRent?.roommateRequirements?.roommateMinAgePreference;
  const displayRenterAgeAndGenderPreferred =
    roommateMinAgePreference === null || roommateMinAgePreference === undefined
      ? `${genderPreferred}`
      : `${roommateMinAgePreference}, ${genderPreferred}`;

  const displayRoomCurrentPets = (() => {
    const displayArr: Array<string> = [];

    // loop through DISPLAY_MAP to specify display order
    Object.keys(CURRENT_PETS_DISPLAY_MAP).forEach((key) => {
      const value = listingDetails?.roomForRent?.currentPets?.[key as keyof typeof CURRENT_PETS_DISPLAY_MAP];

      // accepts a zero (0) value
      if (value !== '' && value !== null && value !== undefined) {
        const containsPlus = value?.includes('+');

        const newValue = containsPlus ? value?.replace('+', '') : value;

        let displayItem = CURRENT_PETS_DISPLAY_MAP[key as keyof typeof CURRENT_PETS_DISPLAY_MAP](String(newValue));

        if (containsPlus) {
          displayItem = displayItem.replace(`${newValue} `, `${newValue}+ `);
        }

        displayArr.push(displayItem);
      }
    });

    return displayArr.join(', ');
  })();

  const displaySpecialOffers = useMemo(() => {
    if (listingDetails?.specialOffers?.length === 0 || !listingDetails?.specialOffers[0]) {
      return null;
    } else {
      return (
        <SpecialOfferItem isFeed={isFeed} listingAlias={listingAlias} specialOffer={listingDetails?.specialOffers[0]} />
      );
    }
  }, [listingDetails?.specialOffers]);

  const canAccessTotalMonthlyPriceWithFee = accessUtils.canAccessTotalMonthlyPriceWithFee({
    isEligible: listingDetails?.totalMonthlyPriceWithFeeNotice?.isEligible,
  });

  return (
    // remove margin bottom from last child
    <Box
      data-testid="RentDetailsContent"
      data-group-title="RentDetails"
      css={{
        '& > :last-child': { marginBottom: 0 },
      }}
    >
      <Box css={reviewSectionStyles} data-testid="MonthlyRent">
        <Box css={reviewTitleRowStyles}>
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            <Box css={iconPlaceholderStyles}>
              {!price && !isDisabled && !isFeed && (
                <AccordionIconError title="Monthly rent incomplete error" marginRight="xs" />
              )}
              {recommendationsBySection[REVIEW_PAGE_SECTIONS.RENT_DETAILS]?.[RECS.PRICE] && (
                <RecommendationIconBulb
                  fieldName={ariaLabels.MONTHLY_RENT}
                  removeFlex={true}
                  onClick={() => {
                    setDisplayRecsModal(RECS.PRICE);
                  }}
                />
              )}
            </Box>
            <Box css={reviewSectionTitleStyles}>
              <Heading level={4} css={{ textStyle: 'body-bold' }}>
                {canAccessTotalMonthlyPriceWithFee ? 'Base' : 'Monthly'} rent
              </Heading>{' '}
              {canAccessTotalMonthlyPriceWithFee && (
                <RMWPopover
                  {...baseRentPopoverContents({
                    currentProperty: listingDetails,
                    user,
                    triggerObject: 'posting_path_review',
                    triggerLocation: 'zrm_review_rent_details_section',
                  })}
                />
              )}
              {recommendationsBySection[REVIEW_PAGE_SECTIONS.RENT_DETAILS]?.[RECS.PRICE] && (
                <TextButton
                  aria-label={`learn-more-about-${ariaLabels.MONTHLY_RENT}`}
                  textStyle="body"
                  onClick={() => {
                    setDisplayRecsModal(RECS.PRICE);
                  }}
                >
                  Learn more
                </TextButton>
              )}
            </Box>
          </Box>

          <ContentSectionEditButton
            buttonFieldName={ariaLabels.MONTHLY_RENT}
            value={
              canAccessTotalMonthlyPriceWithFee
                ? SF_MODAL_COMPONENTS.BaseRentFormField.componentKey
                : SF_MODAL_COMPONENTS.MonthlyRentFormField.componentKey
            }
            onEditClick={onEditClick}
          />
        </Box>
        <FieldValueDisplay value={getMonthlyRentDisplay()} customNoneText={customNoneFieldValueText} />
      </Box>

      <Box css={reviewSectionStyles} data-testid="SpecialOffers">
        <Box css={reviewTitleRowStyles}>
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            <Box css={iconPlaceholderStyles} />
            <Heading level={4} css={{ textStyle: 'body-bold' }}>
              Special offer
            </Heading>
            {listingDetails && user && (
              <InfoPopover
                currentProperty={listingDetails}
                user={user}
                triggerLocation={TRIGGER_LOCATIONS.POSTING_PATH_REVIEW}
              />
            )}
          </Box>
          <ContentSectionEditButton
            buttonFieldName={ariaLabels.SPECIAL_OFFERS}
            value={SF_MODAL_COMPONENTS.SpecialOffersFormField.componentKey}
            onEditClick={onEditClick}
          />
        </Box>
        <FieldValueDisplay customDisplayComponent={displaySpecialOffers} value="" />
      </Box>

      {isRoomForRent && (
        <Box css={reviewSectionStyles} data-testid="RoomUtilitiesAmount">
          <Box css={reviewTitleRowStyles}>
            <Box css={{ display: 'flex', alignItems: 'center' }}>
              <Box css={iconPlaceholderStyles} />
              <Heading level={4} css={{ textStyle: 'body-bold' }}>
                Utilities
              </Heading>
            </Box>

            <ContentSectionEditButton
              buttonFieldName={ariaLabels.UTILITIES}
              value={SF_MODAL_COMPONENTS.RoomUtilitiesFormField.componentKey}
              onEditClick={onEditClick}
            />
          </Box>
          <FieldValueDisplay value={floorplan?.roomForRent?.monthlyUtilitiesShareDescription} />
        </Box>
      )}

      <Box css={reviewSectionStyles} data-testid="SecurityDeposit">
        <Box css={reviewTitleRowStyles}>
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            <Box css={iconPlaceholderStyles}>
              {recommendationsBySection[REVIEW_PAGE_SECTIONS.RENT_DETAILS]?.[RECS.SECURITY_DEPOSIT] && (
                <RecommendationIconBulb
                  fieldName={ariaLabels.SECURITY_DEPOSIT}
                  removeFlex={true}
                  onClick={() => {
                    setDisplayRecsModal(RECS.SECURITY_DEPOSIT);
                  }}
                />
              )}
            </Box>
            <Box css={reviewSectionTitleStyles}>
              <Heading level={4} css={{ textStyle: 'body-bold' }}>
                Security deposit
              </Heading>
              {recommendationTypes.has(RECS.SECURITY_DEPOSIT) && (
                <TextButton
                  aria-label={`learn-more-about-${ariaLabels.SECURITY_DEPOSIT}`}
                  textStyle="body"
                  onClick={() => {
                    setDisplayRecsModal(RECS.SECURITY_DEPOSIT);
                  }}
                >
                  Learn more
                </TextButton>
              )}
            </Box>
          </Box>

          <ContentSectionEditButton
            buttonFieldName={ariaLabels.SECURITY_DEPOSIT}
            value={SF_MODAL_COMPONENTS.SecurityDepositFormField.componentKey}
            onEditClick={onEditClick}
          />
        </Box>
        <FieldValueDisplay value={getSecurityDepositDisplay()} />
        {listingDetails?.rentGuarantee?.isEligible && listingDetails?.rentGuarantee?.isEnabled && (
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            <Box css={iconPlaceholderStyles} />
            <Text textStyle="body-xs" asChild>
              <p>You entered this amount while starting a request for Rental Protection.</p>
            </Text>
          </Box>
        )}
      </Box>

      {isRoomForRent && (
        <Box css={reviewSectionStyles} data-testid="RoommateCount">
          <Box css={reviewTitleRowStyles}>
            <Box css={{ display: 'flex', alignItems: 'center' }}>
              <Box css={iconPlaceholderStyles} />
              <Heading level={4} css={{ textStyle: 'body-bold' }}>
                Roommate count
              </Heading>
            </Box>

            <ContentSectionEditButton
              buttonFieldName={ariaLabels.ROOMMATE_COUNT}
              value={roommateCountComponentKey}
              onEditClick={onEditClick}
            />
          </Box>
          <FieldValueDisplay value={displayRoommateCount} />
        </Box>
      )}

      {isRoomForRent && isRyanFlow && (
        <Box css={reviewSectionStyles} data-testid="RoommateDescription">
          <Box css={reviewTitleRowStyles}>
            <Box css={{ display: 'flex', alignItems: 'center' }}>
              <Box css={iconPlaceholderStyles} />
              <Heading level={4} css={{ textStyle: 'body-bold' }}>
                Roommates description
              </Heading>
            </Box>

            <ContentSectionEditButton
              buttonFieldName={ariaLabels.ROOMMATE_DESCRIPTION}
              value={SF_MODAL_COMPONENTS.RoommateDescriptionFormField.componentKey}
              onEditClick={onEditClick}
            />
          </Box>
          <FieldValueDisplay value={listingDetails?.roomForRent?.currentRoommatesDetails?.roommateDetailsDescription} />
        </Box>
      )}

      {isRoomForRent && isRyanFlow && (
        <Box css={reviewSectionStyles} data-testid="RenterAgeAndGenderPreference">
          <Box css={reviewTitleRowStyles}>
            <Box css={{ display: 'flex', alignItems: 'center' }}>
              <Box css={iconPlaceholderStyles} />
              <Heading level={4} css={{ textStyle: 'body-bold' }}>
                Renter gender preferred
              </Heading>
            </Box>

            <ContentSectionEditButton
              buttonFieldName={ariaLabels.RENTER_AGE_AND_GENDER_PREFERRED}
              value={SF_MODAL_COMPONENTS.GenderPreferenceFormField.componentKey}
              onEditClick={onEditClick}
            />
          </Box>
          <FieldValueDisplay value={displayRenterAgeAndGenderPreferred} />
        </Box>
      )}

      {isRoomForRent && (
        <Box css={reviewSectionStyles} data-testid="RoomCurrentPets">
          <Box css={reviewTitleRowStyles}>
            <Box css={{ display: 'flex', alignItems: 'center' }}>
              <Box css={iconPlaceholderStyles} />
              <Heading level={4} css={{ textStyle: 'body-bold' }}>
                Pets currently on the property
              </Heading>
            </Box>

            <ContentSectionEditButton
              buttonFieldName={ariaLabels.PETS_CURRENTLY_ON_THE_PROPERTY}
              value={SF_MODAL_COMPONENTS.RoomCurrentPetsFormSectionContainer.componentKey}
              onEditClick={onEditClick}
            />
          </Box>
          <FieldValueDisplay value={displayRoomCurrentPets} />
        </Box>
      )}

      {canAccessScreeningCriteria === false && (
        <Box css={reviewSectionStyles} data-testid="Pets">
          <Box css={reviewTitleRowStyles}>
            <Box css={{ display: 'flex', alignItems: 'center' }}>
              <Box css={iconPlaceholderStyles}>
                {!listingDetails?.active && isPetsAllowedEmpty && !isDisabled ? (
                  <AccordionIconError tabIndex={0} title="Pets incomplete error" marginRight="xs" />
                ) : (
                  <>
                    {recommendationsBySection[REVIEW_PAGE_SECTIONS.RENT_DETAILS]?.[RECS.PETS_NOT_ALLOWED] && (
                      <RecommendationIconBulb
                        fieldName={ariaLabelDisplayPets}
                        removeFlex={true}
                        onClick={() => {
                          setDisplayRecsModal(RECS.PETS_NOT_ALLOWED);
                        }}
                      />
                    )}
                  </>
                )}
              </Box>
              <Box css={reviewSectionTitleStyles}>
                <Heading level={4} css={{ textStyle: 'body-bold' }}>
                  {displayPetsHeading}
                </Heading>
                {recommendationTypes.has(RECS.PETS_NOT_ALLOWED) && (
                  <TextButton
                    aria-label={`learn-more-about-${ariaLabelDisplayPets}`}
                    textStyle="body"
                    onClick={() => {
                      setDisplayRecsModal(RECS.PETS_NOT_ALLOWED);
                    }}
                  >
                    Learn more
                  </TextButton>
                )}
              </Box>
            </Box>

            <ContentSectionEditButton
              buttonFieldName={ariaLabelDisplayPets}
              value={SF_MODAL_COMPONENTS.PetPolicyFormField.componentKey}
              onEditClick={onEditClick}
            />
          </Box>
          <FieldValueDisplay value={displayPetsAvailable} customNoneText={customNoneFieldValueText} />
        </Box>
      )}

      {displayRecsModal && (
        <RecommendationModal onClose={() => setDisplayRecsModal('')} recommendationType={displayRecsModal} />
      )}
    </Box>
  );
};

export default RentDetailsContent;
